<template>
  <div class="step-container">
    <span class="step-title">Customize your link</span>
    <div class="step-content">
      <div class="step-layout">
        <div class="link-form">
          <div class="inputs">
            <div class="group cus-group">
              <div class="domain">
                <div @click="$refs.select2.click()" class="select">
                  <label>
                    <span class="label">Domain</span>
                  </label>
                  <select
                    ref="select2"
                    v-model="source.domain"
                    class="standard-select custom-select"
                  >
                    <option
                      v-for="(domain, i) in domains"
                      :value="domain"
                      :key="i"
                    >
                      {{ domain }}/
                    </option>
                  </select>
                  <ChevronDownIcon class="pointer-events-none" />
                  <span class="focus"></span>
                </div>
              </div>
              <div class="path">
                <div class="default-input has-prefix cus-prefix">
                  <label for="path">Link path</label>
                  <div
                    class="input-container custom-input-container"
                    :class="{
                      error:
                        (!path_available && source.path !== setPath) ||
                        !path_valid
                    }"
                  >
                    <input
                      v-model.lazy="source.path"
                      v-debounce="1000"
                      type="text"
                      id="path"
                      autocomplete="off"
                      @keydown.space.prevent
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="group">
              <div>
                <label class="default-input">
                  <span class="label">
                    {{ link.type === "pre-release" ? "Pre-Release" : "" }}
                    Title
                  </span>
                  <input
                    v-model="source.title"
                    type="text"
                    :placeholder="
                      link.type === 'pre-release'
                        ? 'Pre-Release Title'
                        : 'Title'
                    "
                  />
                </label>
              </div>

              <div>
                <label class="default-input">
                  <span class="label">
                    {{ link.type === "pre-release" ? "Pre-Release" : "" }}
                    Subtitle
                  </span>
                  <input
                    v-model="source.subtitle"
                    type="text"
                    :placeholder="
                      link.type === 'pre-release'
                        ? 'Pre-Release Subtitle'
                        : 'Subtitle'
                    "
                  />
                </label>
              </div>
            </div>
            <div class="group" v-if="link.type === 'pre-release'">
              <div>
                <label class="default-input">
                  <span class="label">Post-Release Title</span>
                  <input
                    v-model="source.post_release_title"
                    type="text"
                    placeholder="Post-Release Title"
                  />
                </label>
              </div>
              <div>
                <label class="default-input">
                  <span class="label">Post-Release Subtitle</span>
                  <input
                    v-model="source.post_release_subtitle"
                    type="text"
                    placeholder="Post-Release Subtitle"
                  />
                </label>
              </div>
            </div>
            <div class="group">
              <div>
                <div class="default-input">
                  <span class="label">Switch Header Style</span>
                  <div class="header-styles">
                    <div
                      class="header-style"
                      :class="{ selected: source.cover_image_style === 'full' }"
                      @click="source.cover_image_style = 'full'"
                    >
                      Full
                    </div>
                    <div
                      class="header-style"
                      :class="{
                        selected: source.cover_image_style === 'avatar'
                      }"
                      @click="source.cover_image_style = 'avatar'"
                      v-if="link.type === 'bio'"
                    >
                      Avatar
                    </div>
                    <div
                      class="header-style"
                      :class="{
                        selected: source.cover_image_style === 'square'
                      }"
                      @click="source.cover_image_style = 'square'"
                      v-else
                    >
                      Square
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="group">
              <div class="preview">
                <div @click="$refs.select1.click()" class="select">
                  <label>
                    <span class="label">Cover Media</span>
                  </label>
                  <select
                    ref="select1"
                    v-model="source.preview_type"
                    class="standard-select"
                  >
                    <option value="image">Image</option>
                    <option
                      value="spotify"
                      v-if="source.source_destination.preview"
                      >Spotify</option
                    >
                    <option value="youtube">Youtube</option>
                    <option value="custom">Custom MP3</option>
                  </select>
                  <ChevronDownIcon class="pointer-events-none" />
                  <span class="focus"></span>
                </div>
              </div>
              <div
                v-if="source.preview_type === 'youtube'"
                class="preview-source"
              >
                <label class="default-input">
                  <span v-if="source.youtube_preview_url" class="label"
                    >YouTube URL</span
                  >
                  <input
                    v-model="source.youtube_preview_url"
                    type="text"
                    placeholder="YouTube URL"
                  />
                </label>
              </div>
              <div
                v-if="source.preview_type === 'custom'"
                class="preview-source"
              >
                <label class="default-input">
                  <span v-if="source.custom_preview_url" class="label"
                    >Custom MP3 URL</span
                  >
                  <input
                    v-model="source.custom_preview_url"
                    type="text"
                    placeholder="Custom MP3 URL"
                  />
                </label>
              </div>
              <template v-if="source.preview_type !== 'youtube'">
                <label>
                  <span class="label">Cover Image</span>
                </label>
                <div class="image-source">
                  <div class="image-source-preview">
                    <img :src="linkImage" />
                  </div>
                  <div class="actions">
                    <input
                      type="file"
                      ref="file"
                      @change="onFileChange"
                      v-show="false"
                      accept="image/*"
                    />
                    <button @click="$refs.file.click()" :disabled="uploading">
                      <span v-if="uploading">
                        <font-awesome-icon :icon="['fas', 'rotate']" spin />
                      </span>
                      <span v-else>
                        <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" />
                      </span>
                    </button>
                  </div>
                </div>
              </template>
              <div class="layouts">
                <div @click="$refs.select3.click()" class="select">
                  <label>
                    <span class="label">Theme</span>
                  </label>
                  <select
                    ref="select3"
                    name="select"
                    v-model="layoutTheme"
                    @change="layoutThemeUpdated"
                    class="standard-select"
                  >
                    <option value="2-dark">Flat - Dark</option>
                    <option value="2-light">Flat - Light</option>
                  </select>
                  <ChevronDownIcon class="pointer-events-none" />
                  <span class="focus"></span>
                </div>
              </div>
            </div>

            <label class="switch socials">
              <input
                v-model="source.display_artist_socials"
                type="checkbox"
                class="switch-input"
                checked
              />
              <span
                class="switch-background"
                data-on="On"
                data-off="Off"
              ></span>
              <span class="switch-handle"></span>
              <span class="switch-label">Show Artist Socials</span>
            </label>
          </div>
        </div>
        <div class="landing-page-preview">
          <div v-if="source" class="landing-page-preview-container">
            <LandingPagePreview
              :link="source"
              :destinations="[]"
              v-if="source.layout === 1"
            />
            <LandingPagePreviewTwo
              :link="{
                ...source,
                tracks: [
                  {
                    url:
                      source.source_destination.preview ||
                      source.preview_url ||
                      source.youtube_preview_url ||
                      source.custom_preview_url
                  }
                ],
                artist: {
                  ...(source.artist || {}),
                  socials: this.socials
                }
              }"
              :destinations="edited_destinations"
              :preRelease="true"
              v-if="source.layout === 2"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="step-actions">
      <!--      <button @click="close()" class="button-text">-->
      <!--        <span>Close</span>-->
      <!--      </button>-->
      <div @click="back()" class="back" v-if="link.type === 'pre-release'">
        <ArrowLeftIcon />
        <span class="back-text">BACK</span>
      </div>
      <button @click="next()" class="button-default big next">
        <span>Next</span>
      </button>
    </div>
    <SnackBar>
      <template v-slot:icon></template>
    </SnackBar>
  </div>
</template>
<script>
import ChevronDownIcon from "../../../public/img/icons/chevron-down.svg";
import LandingPagePreview from "@/components/Custom/LandingPagePreview";
import LandingPagePreviewTwo from "@/components/Custom/LandingPagePreviewTwo";
import debounce from "v-debounce";
import {
  M_PATH_AVAILABLE,
  Q_ARTIST_DOMAINS,
  Q_ARTIST_SOCIALS,
  Q_CUSTOM_DOMAIN_ARTISTS_BY_ARTIST,
  Q_GET_SPOTIFY_PREVIEW_URL
} from "@/graphql";
import SnackBar from "@/components/Custom/SnackBar";
import ArrowLeftIcon from "../../../public/img/icons/arrow-left.svg";
export default {
  name: "LinkWizardCustomize",
  components: {
    ArrowLeftIcon,
    ChevronDownIcon,
    SnackBar,
    LandingPagePreview,
    LandingPagePreviewTwo
  },
  directives: {
    debounce
  },
  props: ["action", "destinations", "link"],
  data() {
    return {
      source_link: null,
      source: null,
      domains: ["vibe.to", "listen.to"],
      uploading: false,
      uploadFailed: false,
      path_available: true,
      titleType: "pre-release",
      path_valid: true,
      setPath: null,
      layoutTheme: "",
      edited_destinations: [],
      socials: [],
      destination_defaults_pre_release: {
        spotify: {
          name: "Spotify",
          type: "spotify",
          default_cta: "Pre-Save",
          description: "Spotify Pre-Save",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        applemusic: {
          name: "Apple Music",
          type: "applemusic",
          default_cta: "Pre-Add",
          description: "Apple Music Pre-Add",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        itunes: {
          name: "iTunes",
          type: "itunes",
          default_cta: "Pre-Order",
          description: "iTunes Pre-Order",
          expand: false,
          pre_release: true,
          redirect_url: null
        },
        text: {
          name: "Notify via Text",
          type: "text",
          default_cta: "Notify me via Text",
          description: "Notify via Text",
          expand: false,
          pre_release: true,
          config: {}
        },
        beatport: {
          name: "Beatport",
          type: "beatport",
          default_cta: "Pre-Order",
          description: "Beatport Pre-Order",
          logo:
            "https://artisthub.b-cdn.net/site-icons/dynamic/www.beatport.com.webp",
          expand: false,
          pre_release: true
        },
        custom: {
          name: "Custom",
          type: "custom",
          default_cta: "Pre-Order",
          description: "Custom",
          expand: false,
          pre_release: true
        }
      },
      destination_defaults: {
        spotify: {
          name: "Spotify",
          type: "spotify",
          default_cta: "Stream on Spotify",
          text: "Stream on Spotify"
        },
        applemusic: {
          name: "Apple Music",
          type: "applemusic",
          default_cta: "Stream on Apple Music",
          text: "Stream on Apple Music"
        },
        amazonmusic: {
          name: "Amazon Music",
          type: "amazonmusic",
          default_cta: "Play on Amazon Music",
          text: "Play on Amazon Music"
        },
        deezer: {
          name: "Deezer",
          type: "deezer",
          default_cta: "Play on Deezer",
          text: "Play on Deezer"
        },
        itunes: {
          name: "iTunes",
          type: "itunes",
          default_cta: "Buy on iTunes",
          text: "Buy on iTunes"
        },
        pandora: {
          name: "Pandora",
          type: "pandora",
          default_cta: "Play on Pandora",
          text: "Play on Pandora"
        },
        tidal: {
          name: "Tidal",
          type: "tidal",
          default_cta: "Play on Tidal",
          text: "Play on Tidal"
        },
        soundcloud: {
          name: "SoundCloud",
          type: "soundcloud",
          default_cta: "Play on SoundCloud",
          text: "Play on SoundCloud"
        },
        youtube: {
          name: "YouTube",
          type: "youtube",
          default_cta: "Watch on YouTube",
          text: "Watch on YouTube"
        },
        bandcamp: {
          name: "Bandcamp",
          type: "bandcamp",
          default_cta: "Download on Bandcamp",
          text: "Download on Bandcamp"
        },
        melon: {
          name: "Melon",
          type: "melon",
          default_cta: "Play on Melon",
          text: "Play on Melon"
        },
        resso: {
          name: "Resso",
          type: "resso",
          default_cta: "Play on Resso",
          text: "Play on Resso"
        },
        applepodcasts: {
          name: "Apple Podcasts",
          type: "applepodcasts",
          default_cta: "Listen on Apple Podcasts",
          text: "Listen on Apple Podcasts"
        },
        custom: {
          name: "Custom",
          type: "custom",
          text: "custom",
          logo: null,
          url: null,
          default_cta: "Play",
          cta: "Play",
          enabled: true,
          new: true,
          expand: true
        },
        message_optin: {
          name: "SMS/Email Opt-In",
          type: "message_optin",
          default_cta:
            "Get notified about new releases, merch, and event drops",
          cta: "SMS/Email Opt-In",
          url: "#",
          text: "Get notified about new releases, merch, and event drops",
          description: "SMS/Email Opt-In",
          expand: false,
          configOptions: [
            {
              name: "email_capture",
              type: "toggle",
              label: "Enable Email Capture"
            },
            {
              name: "phone_number_capture",
              type: "toggle",
              label: "Enable Phone Number Capture"
            }
          ],
          config: {
            email_capture: true,
            phone_number_capture: true,
            success_message: ""
          }
        },
        header: {
          name: "Header",
          type: "header",
          default_cta: "Header",
          text: null,
          url: "#"
        },
        spotify_action: {
          name: "Spotify Action",
          type: "spotify_action",
          default_cta: "Save on Spotify",
          text: "Save on Spotify"
        },
        applemusic_action: {
          name: "Apple Music Action",
          type: "applemusic_action",
          default_cta: "Save on Apple Music",
          text: "Save on Apple Music"
        }
      }
    };
  },
  created() {
    let destinations = [];
    const destination_defaults =
      this.link.type === "pre-release"
        ? this.destination_defaults_pre_release
        : this.destination_defaults;

    if (
      this.action === "edit" &&
      !this.$store.state.editLinkWizardData.destinations.length
    ) {
      destinations = this.destinations.length
        ? this.destinations.flatMap(el => {
            if (el.pre_release && this.link.type === "pre-release") {
              return [
                {
                  ...el,
                  description: destination_defaults[el.type].description,
                  logo: el.logo || destination_defaults[el.type].logo,
                  enabled: true,
                  expand: true
                }
              ];
            }

            if (!el.pre_release && this.link.type !== "pre-release") {
              return [
                {
                  ...el,
                  description: destination_defaults[el.type].description,
                  logo: el.logo || destination_defaults[el.type].logo,
                  enabled: true,
                  expand: true
                }
              ];
            }

            return [];
          })
        : [];
    }

    this.source =
      this.action === "edit"
        ? { ...this.$store.state.editLinkWizardData }
        : { ...this.$store.state.createLinkWizardData };

    if (this.link.type !== "pre-release" && this.action === "edit") {
      this.source = {
        ...this.source,
        ...Object.fromEntries(
          Object.entries(this.link).map(([key, value]) => [
            key,
            Array.isArray(value)
              ? !this.source[key] || !this.source[key].length
                ? value
                : this.source[key]
              : !this.source[key]
              ? value
              : this.source[key]
          ])
        ),
        link_type: this.link.type,
        source_destination: { url: "" }
      };
    }

    this.setPath = `${this.source.path}`;

    if (this.source.tracks && this.source.tracks.length) {
      if (this.previewType === "youtube") {
        this.source.youtube_preview_url = this.source.tracks[0].url;
      } else if (this.previewType === "custom") {
        this.source.custom_preview_url = this.source.tracks[0].url;
      }
    }

    this.source.layout = 2;

    if (!this.source.preview_type) this.source.preview_type = "image";

    this.layoutTheme = this.source.layout + "-" + this.source.theme;

    this.edited_destinations = [...destinations];

    if (!destinations.length) {
      for (const [key, destination] of Object.entries(destination_defaults)) {
        if (key === this.source.source_destination.type) {
          this.edited_destinations.push({
            ...destination,
            url: this.source.source_destination.url,
            cta: destination.default_cta,
            text: null,
            enabled: true,
            pre_release: true
          });
        }
      }
    }
  },
  apollo: {
    $subscribe: {
      socials: {
        query: Q_ARTIST_SOCIALS,
        variables() {
          return {
            artist_id: this.artist.id
          };
        },
        result({ data }) {
          this.socials = data.artist_socials;
          this.source.artist = { socials: this.socials };
        }
      },
      subdomains: {
        query: Q_ARTIST_DOMAINS,
        variables() {
          return {
            artist_id: this.artist.id
          };
        },
        result({ data }) {
          if (data.domains.length) {
            const domain = data.domains[0].domain;
            if (!this.domains.includes(domain)) {
              this.domains.push(domain);
            }
          }
        }
      },
      custom_domains: {
        query: Q_CUSTOM_DOMAIN_ARTISTS_BY_ARTIST,
        variables() {
          return {
            artist_id: this.artist.id
          };
        },
        result({ data }) {
          if (data.custom_domain_artists.length) {
            data.custom_domain_artists.forEach(x => {
              const domain = x.custom_domain.domain;
              if (x.custom_domain && x.custom_domain.domain) {
                if (!this.domains.includes(domain)) {
                  this.domains.push(domain);
                }
              }
            });
          }
        }
      }
    }
  },
  computed: {
    commitName() {
      return this.action == "edit"
        ? "setEditLinkWizardData"
        : "setCreateLinkWizardData";
    },
    artist() {
      return this.$store.getters.getArtist;
    },
    createLinkWizardData() {
      return this.$store.getters.getCreateLinkWizardData;
    },
    editLinkWizardData() {
      return this.$store.getters.getEditLinkWizardData;
    },
    linkImage() {
      return this.source.image;
    },
    invalidCover() {
      let invalid = false;
      if (this.source.preview_type === "youtube") {
        return !this.source.youtube_preview_url;
      } else if (this.source.preview_type === "custom") {
        invalid = !this.source.custom_preview_url;
      } else if (this.source.preview_type === "spotify") {
        invalid = !this.source.preview_url;
      }

      return !this.linkImage || invalid;
    },
    domain() {
      if (this.source && this.source.domain) {
        return this.source.domain;
      } else {
        return "vibe.to";
      }
    },
    path() {
      if (this.source && this.source.path) {
        return this.source.path;
      } else {
        return null;
      }
    },
    linkPath() {
      return `${this.domain}/${this.path}`;
    },
    previewType() {
      if (this.source && this.source.preview_type) {
        return this.source.preview_type;
      } else {
        return null;
      }
    },
    title: {
      get() {
        if (this.titleType === "release") {
          return this.source.post_release_title;
        } else {
          return this.source.title;
        }
      },
      set(val) {
        if (this.titleType === "release") {
          this.source.post_release_title = val;
        } else {
          this.source.title = val;
        }
      }
    },
    subtitle: {
      get() {
        if (this.titleType === "release") {
          return this.source.post_release_subtitle;
        } else {
          return this.source.subtitle;
        }
      },
      set(val) {
        if (this.titleType === "release") {
          this.source.post_release_subtitle = val;
        } else {
          this.source.subtitle = val;
        }
      }
    }
  },
  watch: {
    uploadFailed(val) {
      if (val) {
        this.uploading = false;
        this.uploadFailed = false;
      }
    },
    async previewType(newType, oldType) {
      // Do something with search term after it debounced
      console.log(`Preview type changed from ${oldType} to ${newType}`);
      // this.source.preview_url = null;
      // if (oldType !== )
      // try {
      //   const result = await this.$apollo.mutate({
      //     client: "localClient",
      //     mutation: M_PATH_AVAILABLE,
      //     variables: {
      //       domain: this.source.domain,
      //       path: this.path
      //     }
      //   });
      //   console.log("M_PATH_AVAILABLE", result);
      //   this.path_available = result.data.path_available.success;
      // } catch (e) {
      //   console.log(e);
      //   this.path_available = false;
      // }
    },
    async linkPath() {
      // Do something with search term after it debounced
      if (!this.path) return;

      const regexExp = /^[a-zA-Z0-9]+(?:[-_.a-zA-Z0-9]+)*$/g;
      this.path_valid = regexExp.test(this.path);
      if (!this.path_valid) return;

      console.log(`Search term changed to ${this.path}`);

      try {
        const result = await this.$apollo.mutate({
          client: "localClient",
          mutation: M_PATH_AVAILABLE,
          variables: {
            domain: this.source.domain,
            path: this.path
          }
        });
        console.log("M_PATH_AVAILABLE", result);
        this.path_available = result.data.path_available.success;
      } catch (e) {
        console.log(e);
        this.path_available = false;
      }
    }
  },
  methods: {
    getTracks() {
      let url = null;
      if (this.previewType === "spotify") {
        url = this.source.source_destination.preview;
      } else if (
        this.previewType === "youtube" &&
        this.source.youtube_preview_url
      ) {
        url = this.source.youtube_preview_url.replace(
          "youtube.com/shorts/",
          "youtube.com/watch?v="
        );
      } else if (this.previewType === "custom") {
        url = this.source.custom_preview_url;
      }
      if (url) {
        let track = {
          name: this.source.title,
          url: url,
          priority: 1
        };

        if (this.link?.tracks?.length) {
          track.id = this.link.tracks[0].id;
        }

        return [track];
      } else {
        return [];
      }
    },
    back() {
      this.$store.commit(this.commitName, {
        ...this.source,
        image: this.linkImage,
        tracks: this.getTracks()
      });
      this.$emit("back");
    },
    next() {
      if (!this.source.title) {
        alert("Please enter a title");
        return;
      }
      if (!this.source.path) {
        alert("Please enter a path");
        return;
      }
      if (!this.path_valid) {
        alert("Invalid path");
        return;
      }
      if (!this.path_available && this.source.path !== this.setPath) {
        alert("This path is not available");
        return;
      }
      if (!this.path || this.path.length < 3) {
        alert("Path must be at least 3 characters");
        return;
      }

      if (
        this.source.preview_type === "youtube" &&
        !this.source.youtube_preview_url
      ) {
        alert("Please enter a valid YouTube URL");
        return this.$store.dispatch("snack", {
          type: "error",
          text: "Please enter a valid YouTube URL"
        });
      }

      if (
        this.source.preview_type === "custom" &&
        !this.source.custom_preview_url
      ) {
        alert("Please enter a valid Custom MP3 URL");
        return this.$store.dispatch("snack", {
          type: "error",
          text: "Please enter a valid Custom MP3 URL"
        });
      }

      if (
        ["spotify", "image", "custom"].includes(this.source.preview_type) &&
        !this.linkImage
      ) {
        alert("Please upload a cover image");
        return this.$store.dispatch("snack", {
          type: "error",
          text: "Please upload a cover image"
        });
      }

      this.$store.commit(this.commitName, {
        ...this.source,
        image: this.linkImage,
        tracks: this.getTracks()
      });

      let nextStep = 4;
      if (this.action == "edit")
        nextStep = this.link.type === "pre-release" ? 3 : 2;

      this.$emit("setStep", nextStep);
    },
    onFileChange(e) {
      this.uploading = true;
      if (!e.target.files) return;
      this.uploadImage(e.target.files[0]);
    },
    async uploadImage(file) {
      if (file.type.split("/")[0] !== "image") {
        this.uploadFailed = true;
        alert("The file has to be of type 'image'");
        return await this.$store.dispatch("snack", {
          type: "error",
          text: "The file has to be of type 'image'"
        });
      }
      if (file.size > 4100000) {
        this.uploadFailed = true;
        alert("The file can't be larger than 4MB");
        return await this.$store.dispatch("snack", {
          type: "error",
          text: "The file can't be larger than 4MB"
        });
      }

      this.uploadFile(file);
    },
    layoutThemeUpdated(e) {
      const [layout, theme] = e.target.value.split("-");
      this.source.layout = Number(layout);
      this.source.theme = theme;
    },
    uploadFile(file) {
      let _this = this;
      return new Promise(function(resolve, reject) {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('file', file);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              _this.uploading = false;
              const response = JSON.parse(xhr.responseText);
              _this.$store.commit("setCreateLinkWizardImage", response.url);
              _this.source.image = response.url;
              resolve(xhr);
            } else {
              _this.uploadFailed = true;
              _this.$store.dispatch("snack", {
                type: "error",
                text: "Upload failed, please try again."
              });
              reject(xhr);
            }
          }
        };

        xhr.open("POST", "/api/bunny-upload");
        xhr.send(formData);
      });
    },
    getSpotifyPreview() {
      setTimeout(async () => {
        try {
          const r = await this.$apollo.query({
            client: "localClient",
            query: Q_GET_SPOTIFY_PREVIEW_URL,
            variables: {
              link: this.source.preview_source
            }
          });
          console.log("Q_GET_SPOTIFY_PREVIEW_URL", r);
          const { preview_url } = r.data.get_spotify_preview_url;
          console.log(preview_url);
          this.source.preview_url = preview_url;
          // this.source.tracks = [
          //   {
          //     name: this.source.title,
          //     url: preview_url,
          //     priority: 1
          //   }
          // ];
          await this.$store.dispatch("snack", {
            text: "Success! Spotify Preview URL saved."
          });
        } catch (e) {
          console.log(e);
          // await this.$store.dispatch("snack", {
          //   type: "error",
          //   text: "Oops! Invalid URL."
          // });
        }
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.step-container {
  padding-top: 60px;
  width: 100%;

  .step-title {
    display: block;
    text-align: center;
    font-weight: bold;
    font-size: 30px;
    line-height: 42px;
    /* White */
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }

  .step-subtitle {
    display: block;
    text-align: center;
    margin-top: 6px;
    /* 16/Regular */
    font-size: 16px;
    line-height: 24px;
    /* Text/type2 */
    color: $text-type-2;
  }

  .step-content {
    margin-top: 30px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .step-layout {
      max-width: 800px;
      width: 100%;
      display: grid;
      grid-template-columns: auto 340px;
      grid-column-gap: 40px;
      margin-top: 36px;

      .link-form {
        .inputs {
          width: 100%;
          display: flex;
          flex-direction: column;

          .group {
            width: 100%;
            display: flex;
            flex-direction: column;
            padding: 10px;
            border-radius: 10px;
            border: 1px solid #dedcff;

            .header-styles {
              display: flex;
              margin-top: 10px;
              width: 100%;

              .header-style {
                width: 50%;
                padding: 15px;
                cursor: pointer;
                background-color: #100e2a;
                color: #dedcff;
                text-align: center;

                &:first-child {
                  border-radius: 10px 0 0 10px;
                }

                &:last-child {
                  border-radius: 0 10px 10px 0;
                }

                &.selected {
                  background-color: #9c27b0;
                }
              }
            }

            .preview-source {
              .default-input {
                margin-left: 0;
                margin-bottom: 10px;
              }
            }
          }

          div {
            margin: 6px 0;
          }

          .radios {
            display: flex;

            .theme {
              margin-right: 36px;
            }
          }

          .socials {
            grid-area: socials;
            margin-top: 20px;
          }

          label,
          .label {
            margin-left: 5px;
            font-weight: bold;
            font-size: 12px;
            line-height: 15px;
            color: #dedcff;
          }

          .image-source {
            background-color: #100e2a;
            border-radius: 5px;
            padding: 10px;
            margin-top: 10px;
            display: flex;
            justify-content: center;
            border: 1px solid #dedcff;

            .image-source-preview {
              height: 200px;
              width: 200px;
              border-radius: 5px;
              overflow: hidden;

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }

            .actions {
              margin-left: auto;

              button {
                border: 0;
                padding: 0;
                background-color: transparent;
                cursor: pointer;

                svg {
                  width: 20px;
                  height: 20px;
                  color: #9c27b0;
                }
              }
            }
          }
        }

        .select {
          max-width: 100%;
        }
      }

      .landing-page-preview *::-webkit-scrollbar {
        display: none !important;
      }

      .player-container-2 {
        display: none !important;
        overflow: hidden !important;
      }

      .landing-page-preview {
        width: 100%;

        .layouts {
          display: flex;
          /* border: 1px solid #9c27b0; */
          border-radius: 5px;
          margin-bottom: 20px;

          .layout {
            width: 50%;
            font-size: 0.9rem;
            font-weight: 900;
            color: #8786ab;
            padding: 15px;
            text-align: center;
            cursor: pointer;
          }

          .selected {
            background-color: #9c27b0;
            color: white;
          }
        }

        .landing-page-preview-container {
          border-radius: 5px;
          overflow: hidden;
        }
      }
    }
  }

  .step-actions {
    max-width: 800px;
    margin: auto;
    padding-top: 50px;
    padding-bottom: 60px;
    display: flex;
    justify-content: space-between;

    .back {
      cursor: pointer;
      display: flex;
      //flex-direction: column;
      justify-content: flex-start;
      align-items: center;

      &:hover {
        svg {
          path {
            fill: white;
          }
        }

        .back-text {
          color: white;
        }
      }

      svg {
        width: 28px;

        path {
          fill: #8786ab;
        }
      }

      .back-text {
        font-weight: 600;
        font-size: 14px;
        line-height: 14px;
        text-transform: uppercase;
        /* Text/type2 */
        color: $text-type-2;
      }
    }

    .button-default {
      padding: 9px 40px;
    }

    .next {
      margin-left: auto;
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 10;
    }
  }
}
@media (max-width: 768px) {
  .step-container .step-actions {
    max-width: 340px;
  }
}
@media (max-width: 600px) {
  .step-container {
    width: 80%;
  }
  .landing-page-preview {
    display: none;
  }
}
.cus-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row !important;
}
.cus-prefix {
  position: relative;
}
.cus-prefix .errors {
  position: absolute;
  bottom: -22px;
}
.cus-prefix .input-container {
  margin: 0px 0px !important;
}
.player-container-2 {
  overflow: hidden !important;
}
.custom-select {
  border-radius: 5px 0px 0px 5px !important;
  border-right: unset !important;
}
.custom-input-container {
  border-radius: 0px 5px 5px 0px !important;
}
@media screen and (max-width: 1024px) {
  .step-container {
    width: 100%;
  }
  .step-layout {
    grid-column-gap: 20px !important;
  }
}

@media screen and (max-width: 768px) {
  .step-layout {
    display: flex !important;
    flex-direction: column;
    gap: 30px;
    width: 340px !important;
  }
}
</style>
